import React from "react";
import { useParams } from "react-router";
import { Toast } from "@sscs/ui";
import {
  ActionMenu,
  ApprovalDialog,
  Dialog,
  LinkToReport,
  ReanalyzeDialog,
  RevokeDialog,
} from "Components";
import { FILESTREAM_PURL, PERMISSION } from "Constants";
import { useDialog, useReports } from "Hooks";
import { DownloadDialog } from "Modules/download";
// import { getCloudFileDownloader } from "Services";
import { MoveFileToProject } from "Modules/moveFileToProject";
import { useReprocessFileMutation } from "Services";
import { ActionItem, ActionType, PortalFile } from "Types";
import {
  apiErrorToast,
  canFileBeDownloaded,
  isApproved,
  isFileInProgress,
} from "Utils";
import {
  useApproveFileMutation,
  useDeleteFileMutation,
} from "../../services/files.api";

const generateActionMenuItems = (
  file: PortalFile,
  onClick: (action: ActionType) => void,
): ActionItem[] => {
  const items: ActionItem[] = [
    {
      id: "move-to-project",
      type: "MOVE_TO_PROJECT",
      text: "Move to project",
      isDisabled: isFileInProgress(file.current_status),
      onClick: () => onClick("MOVE_TO_PROJECT"),
      permissions: [PERMISSION.UPLOAD_VERSION],
    },
    {
      id: "view-report",
      type: "VIEW_REPORT",
      text: (
        <LinkToReport
          id="view-report"
          type="text"
          report={{
            isRepro: undefined,
            packageName: FILESTREAM_PURL.packageName,
            projectName: FILESTREAM_PURL.projectName,
            version: file.id,
          }}
        >
          View report
        </LinkToReport>
      ),
      isDisabled: !file.latest_report_summary?.report_id,
      onClick: () => onClick("VIEW_REPORT"),
    },
    {
      id: "reanalyze",
      type: "REANALYZE",
      text: "Reanalyze",
      isDisabled: isFileInProgress(file.current_status),
      onClick: () => onClick("REANALYZE"),
      permissions: [PERMISSION.REPROCESS_FILE],
    },
    {
      id: "download",
      type: "DOWNLOAD",
      text: "Download file",
      isDisabled: !canFileBeDownloaded(file.current_status),
      onClick: () => onClick("DOWNLOAD"),
      permissions: [
        PERMISSION.MODIFY_FILE_APPROVAL,
        PERMISSION.DOWNLOAD_FILESTREAM_FILES,
      ],
    },
    {
      id: "delete-file",
      type: "DELETE",
      text: "Delete file",
      onClick: () => onClick("DELETE"),
      permissions: [PERMISSION.DELETE_FILE],
    },
  ];

  if (
    file.approval_status === "Undetermined" ||
    file.approval_status === "ApprovalRejected"
  ) {
    items.push({
      id: "approve-file",
      type: "APPROVAL",
      text: "Approve/Reject",
      isDisabled: file.current_status.code !== "DONE",
      onClick: () => onClick("APPROVAL"),
      permissions: [PERMISSION.MODIFY_FILE_APPROVAL],
    });
  } else if (isApproved(file.approval_status)) {
    items.push({
      id: "revoke-file-approval",
      type: "REVOKE_APPROVAL",
      text: "Revoke approval",
      permissions: [PERMISSION.MODIFY_FILE_APPROVAL],
      onClick: () => onClick("REVOKE_APPROVAL"),
      isDisabled: file.current_status.code !== "DONE",
    });
  }

  return items;
};

type FileActionsProps = {
  rowIndex: number;
  file: PortalFile;
};

const FileActions = ({
  rowIndex,
  file,
}: FileActionsProps): React.JSX.Element => {
  const { groupId } = useParams();

  const deleteDialog = useDialog();
  const approvalDialog = useDialog();
  const revokeDialog = useDialog();
  const downloadDialog = useDialog();
  const moveToProjectDialog = useDialog();
  const reanalyzeDialog = useDialog();

  const [reanalyzeFileMutation] = useReprocessFileMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [editApproval] = useApproveFileMutation();
  const { closeAllReports } = useReports();

  /* TODO: Once we resolve issues with CSP and leaking of S3 bucket names
   * download will be done without users having to do an extra click.
   * For that reason, this code will remain commented out until we revert back to it.
   */
  // const { handleDownload } = getCloudFileDownloader(
  //   file.id,
  //   file.file_name,
  //   "FILESTREAM"
  // );

  const reanalyzeFile = async (notifyOnCompletion: boolean) => {
    try {
      await reanalyzeFileMutation({
        fileId: file.id,
        notify_on_completion: notifyOnCompletion,
      }).unwrap();
      Toast.show({
        title: "File sent for reanalysis",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not reanalyze file");
    }
  };

  const handleMenuClick = async (action: ActionType) => {
    switch (action) {
      case "MOVE_TO_PROJECT":
        moveToProjectDialog.open();
        break;
      // handled by link component
      case "VIEW_REPORT":
        break;
      case "DELETE":
        deleteDialog.open();
        break;
      case "REANALYZE":
        reanalyzeDialog.open();
        break;
      case "APPROVAL":
        approvalDialog.open();
        break;
      case "REVOKE_APPROVAL":
        revokeDialog.open();
        break;
      case "DOWNLOAD":
        // handleDownload();
        downloadDialog.open();
        break;
    }
  };

  const actionItems = generateActionMenuItems(file, handleMenuClick);

  return (
    <>
      <ActionMenu id={`file-actions-${rowIndex}`} items={actionItems} />
      <ReanalyzeDialog
        fileName={file.file_name}
        isOpen={reanalyzeDialog.isOpen}
        onClose={reanalyzeDialog.close}
        onConfirm={reanalyzeFile}
      />
      <Dialog
        title="Delete file?"
        confirmText="Delete"
        cancelText="No"
        onConfirm={async () => {
          try {
            await deleteFile(file.id).unwrap();

            Toast.show({ title: "File deleted", type: "success" });
          } catch (error) {
            apiErrorToast(error, "Could not delete file");
            deleteDialog.close();
            return;
          }

          closeAllReports({
            groupId,
            projectName: FILESTREAM_PURL.projectName,
            packageName: FILESTREAM_PURL.packageName,
            version: file.id,
          });
        }}
        onCancel={deleteDialog.close}
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        cancelButtonColor="primary"
        confirmButtonColor="secondary"
      >
        <span>
          Are you sure you want to delete this file and its SDLC report?
        </span>
      </Dialog>
      <ApprovalDialog
        onClose={approvalDialog.close}
        isOpen={approvalDialog.isOpen}
        subjectId={file.id}
        editApproval={editApproval}
      />
      <RevokeDialog
        onClose={revokeDialog.close}
        isOpen={revokeDialog.isOpen}
        subjectId={file.id}
        editApproval={editApproval}
      />
      <DownloadDialog
        onClose={downloadDialog.close}
        isOpen={downloadDialog.isOpen}
        fileID={file.id}
        fileType="FILESTREAM"
      />
      <MoveFileToProject
        isOpen={moveToProjectDialog.isOpen}
        onClose={(isMoved) => {
          moveToProjectDialog.close();
          if (isMoved === true) {
            closeAllReports({
              groupId,
              projectName: FILESTREAM_PURL.projectName,
              packageName: FILESTREAM_PURL.packageName,
              version: file.id,
            });
          }
        }}
        file={file}
        linkToProject
      />
    </>
  );
};

export default FileActions;
